import { RequestFormEndpoints } from '../types/endpoint.type'
import {
  IDevice,
  IStationClass,
  IEmissionType,
  IDeviceAntenna,
  INumberOfStations,
  IRadarTunabilityTypes,
  IRequestEquipment,
  IRequestStationClass,
  IEmissionDesignatorSimpleType,
  IRequestDeviceAntenna,
} from '../types/equipment.type'
import { IResponse } from '../types/response.type'
import { axiosInstance } from './axios.service'

export const getAllEquipments = async (id: number): Promise<IResponse<IRequestEquipment[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_EQUIPMENTS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createExerciseEquipment = async (id: number): Promise<IResponse<IRequestEquipment>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_EQUIPMENTS, {
      exerciseRequestId: id,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateEquipmentById = async (
  id: number,
  params: IRequestEquipment,
): Promise<IResponse<IRequestEquipment>> => {
  try {
    const data = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_EQUIPMENTS}/${id}`, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteEquipmentById = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_EQUIPMENTS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllDevices = async (): Promise<IResponse<IDevice[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_DEVICES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllNumberOfStations = async (): Promise<IResponse<INumberOfStations[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_NUMBER_OF_STATIONS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllRadarTunabilityTypes = async (): Promise<IResponse<IRadarTunabilityTypes[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_RADAR_TUNABILITY_TYPES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}
export const getAllStationClassDescriptions = async (): Promise<IResponse<IStationClass[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.STATION_CLASS_DESCRIPTION)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllEmissionDesignatorSimpleTypes = async (): Promise<IResponse<IEmissionDesignatorSimpleType[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.EMISSION_DESIGNATOR_SIMPLE_TYPES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllEmissionTypes = async (): Promise<IResponse<IEmissionType[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.EMISSION_TYPES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllStationClasses = async (): Promise<IResponse<IStationClass[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.STATION_CLASS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllRequestStationClasses = async (id: number): Promise<IResponse<IRequestStationClass[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_STATION_CLASS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createRequestStationClass = async (id: number): Promise<IResponse<IRequestStationClass>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_STATION_CLASS, {
      deviceId: id,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateRequestStationClassById = async (
  id: number,
  params: {
    station_class_id: number | null
    station_class_type: string | null
    power: number
    power_units: 'W' | 'KW' | 'MW' | 'GW' | null
    flight_level: number
    modulation_id: number | null
    traffic_id: number | null
    is_traffic_encrypted: boolean
    antenna_radius: number
    antenna_feed_point_height: number
    emission_type_id: number | null
    receiving_unit_tier_id: number | null
    operating_unit_other: string | null
    emission_designator_simple_type_id: number | null
    emission_designator_id: number | null
  },
): Promise<IResponse<IRequestStationClass>> => {
  try {
    const resp = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_STATION_CLASS}/${id}`, params)

    return resp.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteRequestStationClassById = async (id: number): Promise<IResponse<null>> => {
  try {
    const resp = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_STATION_CLASS}/${id}`)

    return resp.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllRequestDeviceAntennas = async (id: number): Promise<IResponse<IRequestDeviceAntenna[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_DEVICE_ANTENNA}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createRequestDeviceAntenna = async (equipmentId: number): Promise<IResponse<IRequestDeviceAntenna>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_DEVICE_ANTENNA, { equipmentId })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateRequestDeviceAntennaById = async (
  id: number,
  params: {
    antenna_structure_height: number
    device_antenna_id: number
  },
): Promise<IResponse<IRequestDeviceAntenna>> => {
  try {
    const data = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_DEVICE_ANTENNA}/${id}`, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteRequestDeviceAntennaById = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_DEVICE_ANTENNA}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getALlDeviceAntennas = async (id: number): Promise<IResponse<IDeviceAntenna[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.GET_DEVICE_ANTENNAS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}
